import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { FlaggedPageData } from '@/lib/directory'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/directory/page-hero'
import { RankedToolList } from '@/components/directory/ranked-tool-row'
import { CategoryNav } from '@/components/directory/category-nav'
import { RankingStats } from '@/components/directory/ranking-stats'
import { iconFor } from '@/components/directory/category-icon'
import { getFlaggedPage } from '@/lib/directory'

interface FreeToolsPageProps {
    data: FlaggedPageData
}

const FreeToolsPage: FC<FreeToolsPageProps> = ({ data }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Free AI video and image tools'
                description='AI video and image tools with real free plans or free daily credits, ranked by score.'
            />

            <PageHero
                breadcrumbs={[{ href: '/#categories', label: 'Categories' }, { label: 'Free AI Tools' }]}
                icon={iconFor('free-tools')}
                title='Free AI Tools'
                intro={<p>Real free tiers you can use before a card ever comes out: free plans, daily credits and open models. Free limits change often, so check the current allowance on each site.</p>}
            >
                <RankingStats tools={data.tools} />
            </PageHero>

            <div className='mx-auto grid max-w-7xl gap-8 px-4 pt-10 sm:px-6 lg:grid-cols-[1fr_280px] lg:px-8'>
                <section id='ranking' aria-labelledby='ranking-heading'>
                    <h2 id='ranking-heading' className='sr-only'>Ranking</h2>
                    <RankedToolList tools={data.tools} emptyMessage='No free tools are listed yet.' />
                </section>

                <aside className='lg:sticky lg:top-24 lg:self-start'>
                    <CategoryNav categories={data.categories} activeKey='free-tools' />
                </aside>
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<FreeToolsPageProps> = async () => {
    try {
        const data = await getFlaggedPage('is_free')

        return { props: { data }, revalidate: 60 }
    }
    catch (error) {
        console.error('Free tools page data failed to load:', error instanceof Error ? error.message : error)

        return { props: { data: { tools: [], categories: [] } }, revalidate: 30 }
    }
}

export default FreeToolsPage
